import { defineStore } from "pinia";
import { ref, computed, nextTick } from "vue";
import { Toast } from "bootstrap";
import cookie from "@/utilities/cookie.js";

export const useRewardSetupStore = defineStore("rewardSetup", () => {
  const campaignType = ref("");
  const cashRebate = ref(0);
  const esbItemCode = ref(null);
  const discountLimit = ref(0);
  const ecomIntegration = ref(null);
  const rewardName = ref("");
  const rewardCost = ref(0);
  const rewardType = ref("");
  const posIntegration = ref(null);
  const pointsRequired = ref(0);
  const rewardCriteria = ref(null);
  const rewardExpiry = ref(10);
  const shopifyProductId = ref(null);
  const eats365ProductCode = ref(null);
  const hasPOSReward = ref(false);
  const numOfPOSCodes = ref(0);
  const integrationItemCode = ref(null);
  const posRewardType = ref(null);
  const posRewardName = ref(null);
  const allRewards = ref([]);
  const email = cookie.getSecureCookie("mulah_em");
  const expandFirstHistoryRow = ref(false);
  const rewardDisplayName = ref("");
  const showRewardDisplayInput = ref(false);
  const stampBackgroundColor = ref("#FFFFFF");
  const stampTextColor = ref("#000000");
  const rewardDisplayImage = ref(null);
  const rewardDisplayIcon = ref("");
  const isRewardImage = ref(false);
  const validRewardTypes = ref([
    "percentage_discount",
    "free_shipping",
    "free_item",
    "cash_discount",
    "custom",
    "point_multiplier",
  ]);

  const rewardDisplayText = computed(() => {
    return rewardName.value.slice(0, 10).toUpperCase() || "REWARD";
  });

  const formatRewardType = (type) => {
    if (type === "free_shipping") {
      return "Free Shipping";
    } else if (type === "percentage_discount") {
      return "Percentage Discount";
    } else if (type === "free_item") {
      return "Free Item";
    } else {
      return "Cash Discount";
    }
  };

  const getSingleRewardPOSText = (
    text,
    pos = null, // 'Xilnex' or 'Zeoniq'
    code = null,
    name = null,
    type = null
  ) => {
    if (!pos) return "";
    const posName = pos.charAt(0).toUpperCase() + pos.slice(1).toLowerCase();
    const instruction = `
      A copy of the instructions have been sent to ${email}. Please complete the set up on your POS Dashboard before members begin redeeming rewards.`;

    if (posName.toLowerCase() === "zeoniq") {
      const posText = {
        header: `${posName} Voucher Codes Generated`,
        bodyHeader: `Your ${posName} Voucher Code(s) has been generated for ${name}.`,
        bodyStepOne: "",
        bodyStepTwo: "",
        instructions: instruction,
      };
      return posText[text];
    }

    // Xilnex
    if (posName.toLowerCase() === "xilnex") {
      const posText = {
        header: `${posName} Item Code - ${code}`,
        bodyHeader: `For this reward to be redeemable on your POS, you will need to input the Xilnex item Code into Your Xilnex Dashboard.`,
        bodyStepOne: `This is the Xilnex Item Code - ${code} For ${name} (${formatRewardType(
          type
        )})`,
        bodyStepTwo: "",
        instructions: instruction,
      };
      return posText[text];
    }

    if (posName.toLowerCase() === "eats365") {
      const posText = {
        header: `Eats365 Item SKU – ${code}`,
        bodyHeader: `Make sure an item with SKU ${code} exists in your Eats365 POS so members can redeem ${name} (${formatRewardType(
          type
        )}).`,
        bodyStepOne: "",
        bodyStepTwo: "",
        instructions: "",
      };
      return posText[text];
    }

    if (posName.toLowerCase() === "esb") {
      const posText = {
        header: `ESB Promotion Code - ${code}`,
        bodyHeader: `${code} - Promotion Code for ${name} (${formatRewardType(
          type
        )}) on ESB Dashboard`,
        bodyStepOne: "Create Promotion on ESB Dashboard",
        bodyStepTwo: `Link ${code} to the Promotion created`,
        instructions: instruction,
      };
      return posText[text];
    }

    // Other POS fallback
    const posText = {
      header: `${posName} Item Code Generated - ${code}`,
      bodyHeader: `${code} - Item Code for ${name} (${formatRewardType(
        type
      )}) on ${posName} Dashboard`,
      bodyStepOne: `1. Create Promotion on ${posName} Dashboard`,
      bodyStepTwo: `2. Link ${code} to the Promotion created`,
      instructions: instruction,
    };
    return posText[text];
  };

  const getMultipleRewardsPOSText = (
    text,
    pos = null, // 'Xilnex' or 'Zeoniq'
    numOfCodes = null,
    campaign = null,
    codes = []
  ) => {
    if (!pos) return "";

    const posName = pos.charAt(0).toUpperCase() + pos.slice(1).toLowerCase();
    const instruction = `
      A copy of the instructions have been sent to ${email}. Please complete the set up on your POS Dashboard before members begin redeeming rewards.`;

    if (posName.toLowerCase() === "zeoniq") {
      const posText = {
        header: `${posName} Voucher Codes Generated`,
        bodyHeader: `Your ${posName} Voucher Code(s) have been generated for ALL rewards of ${campaign}.`,
        bodyStepOne: "",
        bodyStepTwo: "",
        instructions: instruction,
      };
      return posText[text];
    }

    if (posName.toLowerCase() === "eats365") {
      const isSingle = codes.length === 1;
      const posText = {
        header: isSingle
          ? `Eats365 Item SKU – ${codes[0]}`
          : "Eats365 Item SKUs Added",
        bodyHeader: isSingle
          ? `Make sure an item with SKU ${codes[0]} exists in your Eats365 POS.`
          : `Make sure items with these SKUs exist in your Eats365 POS: ${codes.join(
              ", "
            )}`,
        bodyStepOne: "",
        bodyStepTwo: "",
        instructions: "",
      };
      return posText[text];
    }

    if (posName.toLowerCase() === "esb") {
      const posText = {
        header: `${numOfCodes} ESB Promotion Codes Generated`,
        bodyHeader: `${numOfCodes} Promotion Codes have been generated for ALL rewards of ${campaign}.`,
        bodyStepOne: `1. Create ${numOfCodes} Promotions on ESB Core, one for each Item Code`,
        bodyStepTwo: `2. Link each Item Code to the respective Promotion created`,
        instructions: instruction,
      };
      return posText[text];
    }

    // Xilnex or other POS fallback
    const posText = {
      header: `${numOfCodes} ${posName} Item Codes Generated`,
      bodyHeader: `${numOfCodes} Item Codes have been generated for ALL rewards of ${campaign}.`,
      bodyStepOne: `1. Create ${numOfCodes} Promotions on ${posName} Dashboard, one for each Item Code`,
      bodyStepTwo: `2. Link each Item Code to the respective Promotion created`,
      instructions: `We will email you the full guide soon. Please integrate quickly before members start redeeming rewards!`,
    };
    return posText[text];
  };

  const getRewardPosInstructions = (pos) => {
    if (!pos) return "";
    let url = "";
    const posName = pos.toLowerCase();

    switch (posName) {
      case "zeoniq":
        url =
          "https://docs.google.com/document/d/1x622TUd113JeXCfTC3FPr-iiIWL81aP_uA5iVnPlDFE/edit?tab=t.0";
        break;
      case "esb":
        url =
          "https://docs.google.com/document/d/1UubP4xCC-kXrn-ckvs-GxEo3buxZO8TT2e5772cdDfk/edit?tab=t.jm9ghkvg7gsp#heading=h.mfzi2yb3pkcx";
        break;
      case "xilnex":
        url =
          "https://docs.google.com/document/d/1u8WJ-3kg3ZdgUE7IvMmrntdMBN-ORD1beYs5vST1rtM/edit?tab=t.0";
        break;
    }

    return url;
  };

  const isEats365SkuReward = (pos, type, sku) =>
    pos === "eats365" && type === "free_item" && !!sku;

  const triggerToast = async () => {
    await nextTick();
    const posToastElement = document.getElementById("pos-toast");
    if (posToastElement) {
      const toast = new Toast(posToastElement, { delay: 15000 });
      toast.show();
    }
    await nextTick();
    if (campaignType.value !== "google") {
      resetStore();
    }
  };

  const canContinue = computed(() => {
    if (!validRewardTypes.value.includes(rewardType.value)) {
      return false;
    }

    //Shopify Free Item check
    if (
      rewardType.value === "free_item" &&
      ecomIntegration.value === "shopify" &&
      !shopifyProductId.value
    ) {
      return false;
    }

    // Shopify Cash Rebate check
    if (
      rewardType.value === "cash_discount" &&
      (ecomIntegration.value === "shopify" ||
        posIntegration.value === "esb" ||
        posIntegration.value === "zeoniq" ||
        posIntegration.value === "eats365") &&
      !(cashRebate.value > 0)
    ) {
      return false;
    }

    if (
      rewardType.value === "free_item" &&
      posIntegration.value === "esb" &&
      !esbItemCode.value
    ) {
      return false;
    }

    if (
      rewardType.value === "free_item" &&
      posIntegration.value === "eats365" &&
      !eats365ProductCode.value
    ) {
      return false;
    }

    return true;
  });

  const resetStore = () => {
    campaignType.value = "";
    cashRebate.value = 0;
    esbItemCode.value = null;
    discountLimit.value = 0;
    ecomIntegration.value = null;
    rewardName.value = "";
    rewardCost.value = 0;
    rewardType.value = "";
    posIntegration.value = null;
    pointsRequired.value = 0;
    rewardCriteria.value = null;
    rewardExpiry.value = 0;
    hasPOSReward.value = false;
    shopifyProductId.value = null;
    eats365ProductCode.value = null;
    numOfPOSCodes.value = 0;
    rewardDisplayName.value = "";
    showRewardDisplayInput.value = false;
    integrationItemCode.value = null;
    posRewardType.value = null;
    posRewardName.value = null;
    allRewards.value = [];
    stampBackgroundColor.value = "#FFFFFF";
    rewardDisplayImage.value = null;
    isRewardImage.value = false;
    rewardDisplayIcon.value = "";
    stampTextColor.value = "#000000";
  };

  const isDuplicateReward = computed(() => {
    const normalizedInput = rewardName.value.trim().toLowerCase();
    return allRewards.value?.some(
      (item) => item.title.trim().toLowerCase() === normalizedInput
    );
  });

  return {
    campaignType,
    cashRebate,
    esbItemCode,
    discountLimit,
    rewardCriteria,
    rewardExpiry,
    rewardName,
    rewardCost,
    rewardType,
    pointsRequired,
    posIntegration,
    ecomIntegration,
    shopifyProductId,
    eats365ProductCode,
    canContinue,
    hasPOSReward,
    numOfPOSCodes,
    integrationItemCode,
    posRewardType,
    posRewardName,
    allRewards,
    triggerToast,
    isEats365SkuReward,
    formatRewardType,
    getSingleRewardPOSText,
    getMultipleRewardsPOSText,
    getRewardPosInstructions,
    resetStore,
    isDuplicateReward,
    expandFirstHistoryRow,
    rewardDisplayName,
    showRewardDisplayInput,
    stampBackgroundColor,
    rewardDisplayImage,
    rewardDisplayText,
    isRewardImage,
    rewardDisplayIcon,
    stampTextColor,
    validRewardTypes,
  };
});
